/**
 * Invoices list.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { CalendarRange, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { InvoiceStatusPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { invoicesApi, type NerdInvoice } from "@/lib/api-jobs";
import { formatCurrency, formatDate } from "@/lib/format";

export function InvoicesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["invoices"], queryFn: () => invoicesApi.list({ size: 200 }) });

  const genMonth = useMutation({
    mutationFn: () => {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return invoicesApi.generateMonth(prev.getFullYear(), prev.getMonth() + 1, false);
    },
    onSuccess: (r) => {
      const created = (r.created as number) ?? 0;
      const skipped = (r.skipped_existing as number) ?? 0;
      toast.success(`Faturas geradas: ${created} (${skipped} já existiam)`);
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: () => toast.error("Falha ao gerar faturas do mês"),
  });

  const columns: ColumnDef<NerdInvoice>[] = [
    {
      id: "code",
      accessorKey: "code",
      header: "Código",
      cell: ({ row }) => (
        <span className="font-mono text-sm font-medium">{row.original.code}</span>
      ),
    },
    {
      id: "contract_type",
      accessorKey: "contract_type",
      header: "Tipo",
      cell: ({ row }) => (
        <Badge variant="default" className="capitalize">{row.original.contract_type}</Badge>
      ),
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <InvoiceStatusPill status={row.original.status} />,
    },
    {
      id: "services_total",
      accessorKey: "services_total",
      header: "Serviços",
      cell: ({ row }) => (
        <span className="tabular-nums">{formatCurrency(row.original.services_total)}</span>
      ),
    },
    {
      id: "reimbursement_total",
      accessorKey: "reimbursement_total",
      header: "Reembolsos",
      cell: ({ row }) => (
        <span className="tabular-nums">{formatCurrency(row.original.reimbursement_total)}</span>
      ),
    },
    {
      id: "total",
      accessorKey: "total",
      header: "Total",
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums">{formatCurrency(row.original.total)}</span>
      ),
    },
    {
      id: "scheduled_payment_date",
      accessorKey: "scheduled_payment_date",
      header: "Pagamento",
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.scheduled_payment_date)}</span>
      ),
    },
    {
      id: "advance_requested",
      accessorKey: "advance_requested",
      header: "Antecipação",
      cell: ({ row }) =>
        row.original.advance_processed ? (
          <Badge variant="success">Processada</Badge>
        ) : row.original.advance_requested ? (
          <Badge variant="warning">Solicitada</Badge>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Faturas"
        description="Faturamento dos prestadores — NF, programação e antecipação Loopi."
        actions={
          <Button onClick={() => genMonth.mutate()} disabled={genMonth.isPending}>
            <CalendarRange className="h-4 w-4" /> Gerar faturas do mês
          </Button>
        }
      />
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar fatura..."
        emptyTitle="Nenhuma fatura emitida"
        emptyDescription="Faturas são criadas após chamados concluídos pelo nerd."
        onRowClick={(i) => navigate(`/invoices/${i.id}`)}
        columnLabels={{
          code: "Código",
          contract_type: "Tipo",
          status: "Status",
          services_total: "Serviços",
          reimbursement_total: "Reembolsos",
          total: "Total",
          scheduled_payment_date: "Pagamento",
          advance_requested: "Antecipação",
        }}
      />

      {list.data?.items.length === 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Receipt className="h-3.5 w-3.5" />
          Faturas são criadas via POST /v1/invoices a partir de jobs com status COMPLETED ou APPROVED.
        </div>
      )}
    </>
  );
}
